import { platformFor, type PlatformName } from '@deepdive-icons';
import styles from './PlatformIcon.module.css';

export type PlatformIconProps = {
  /**
   * The platform — a `PlatformName`, or any channel key. A key with no drawn
   * mark (a live channel's id) is looked up by `label` instead; with neither,
   * the label is shown as text, and without a label nothing is drawn.
   */
  name: PlatformName | (string & {});
  /**
   * Accessible name. Omit for decorative marks (the default) — correct wherever
   * an adjacent label already names the channel.
   */
  label?: string;
  className?: string;
};

/**
 * A 12x12 full-colour platform mark from Figma's `_Legend`
 * `Orientation=Platforms` variant (893:32882).
 *
 * The sibling of `Icon`, not a variant of it. `Icon` paints every path in
 * `currentColor` so the `icon/*` tokens can drive it; a brand mark carries its
 * own colours and must not be tinted, so this one does not touch fill at all.
 *
 * The markup is injected rather than mapped over a path list because these are
 * not path lists: Instagram is two radial gradients under a `<defs>`, and TikTok
 * and Reddit are multi-fill compositions. `Icon`'s `paths.map` cannot express
 * either. The strings are build-time constants exported from our own design
 * file and interpolate nothing — no user input reaches this, which is the whole
 * risk `dangerouslySetInnerHTML` names.
 */
export function PlatformIcon({ name, label, className }: PlatformIconProps) {
  const definition = platformFor(name) ?? (label ? platformFor(label) : undefined);
  /* An unknown channel degrades to its name, as ChartLegend does — never a crash. */
  if (!definition) return label ? <span className={className}>{label}</span> : null;
  const decorative = label === undefined;

  return (
    <svg
      className={className ? `${styles.platformIcon} ${className}` : styles.platformIcon}
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={decorative ? undefined : 'img'}
      aria-label={label}
      aria-hidden={decorative || undefined}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: definition.markup }}
    />
  );
}
